import { PendingItemDto } from '@act/shared/data-transfer-objects';
import { useEffect, useMemo, useState } from 'react';
import { docketActions, selectTabPreferences } from 'redux/slices/docketSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useDocketSelectors } from 'redux/utils/selectors/docket-selectors';
import {
  PendingItemFilters,
  filterChoices,
  isPendingItemInFilter,
} from 'redux/utils/selectors/pending-item-selectors';
import {
  FilterChips,
  FlexContainer,
  DocketMobileCard,
  DocketMobilePagination,
} from '@act/storybook';
import ScrollableTable, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS as rowsPerPageOptions,
} from './ScrollableTable';
import { HeaderCellConfig } from 'toolkit/tables/types/header.types';
import {
  PaginationBounds,
  SortParameters,
} from 'toolkit/tables/types/table.types';
import PendingItemTableRow from './pending-item-table-row';
import {
  convertToPendingItemWithMember,
  PendingItemWithMember,
} from './utilities/expandedPendingItem.type';
import { PendingColumnKeys } from './utilities/pendingColumnKeys.type';
import { getPendingItemComparator } from './utilities/sortPendingUtils';
import { useMobile } from '@act/utilities/mobile-context';
import { DateTime } from 'luxon';
import { useAppNavigation } from 'react-components/app/routing/routes';
import useInteractionsDialog from '../../dialog-forms/hooks/use-interactions-dialog';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import { selectScheduledEventById } from 'redux/slices/scheduledEventSlice';
import { useApiCall } from 'redux/utils/api-hook';
import { Menu, MenuItem } from '@mui/material';

const headerCellConfig: HeaderCellConfig<PendingColumnKeys>[] = [
  {
    key: 'member',
    label: 'Member',
    sortable: true,
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
  },
  {
    key: 'event',
    label: 'Event',
    sortable: true,
  },
  {
    key: 'time',
    label: 'Date / time',
    sortable: true,
  },
  {
    key: 'ellipsis-menu',
    width: 56,
  },
  {
    key: 'link-to-feed',
    width: 56,
  },
];

const CACHED_TAB_KEY: string = 'late-events';

const eventTypeToLabel = {
  call: 'Call',
  message: 'Message',
  visit: 'Visit',
  workflow_phase: 'Workflow',
  transfer_request: 'Transfer request',
  status_change_request: 'Status change request',
};

// Helper function to format time for mobile cards (with date)
const formatLateItemTime = (
  item: PendingItemWithMember,
  scheduledEvent?: unknown
) => {
  // Handle null timestamp
  if (!item.timestamp) {
    return { date: '––', time: '' };
  }

  const dateTime = DateTime.fromMillis(item.timestamp);
  if (
    item.externalItemType === 'scheduled_event' &&
    scheduledEvent &&
    !scheduledEvent['time']
  ) {
    return { date: dateTime.toFormat('LLL dd'), time: '' };
  }
  return {
    date: dateTime.toFormat('LLL dd'),
    time: dateTime.toFormat('h:mma'),
  };
};

const DocketLateEvents = ({ guideId }: { guideId: string }) => {
  const { isMobile } = useMobile();
  const [filters, setFilters] = useState<PendingItemFilters[]>([]);
  const [sortParams, setSortParams] =
    useState<SortParameters<PendingColumnKeys>>();
  const latePendingItems: PendingItemDto[] =
    useDocketSelectors.latePendingItemsForGuide(guideId);
  const loading = useDocketSelectors.pendingItemsLoading();
  const memberMap = membersAppSelectors.useSelectMembersMap();
  const dispatch = useAppDispatch();

  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedMobileItem, setSelectedMobileItem] =
    useState<PendingItemWithMember | null>(null);

  const { toMemberPage } = useAppNavigation();
  const interactionsDialogHelper = useInteractionsDialog();
  const [_, __, deleteScheduledEvent] = useApiCall(
    scheduledEventRequests.deleteScheduledEventForUser
  );

  // Get scheduled events state once at top level
  const scheduledEventsState = useAppSelector(state => state.scheduledEvents);

  // Function to get scheduled event by ID (not a hook)
  const getScheduledEvent = (eventId: number) => {
    return selectScheduledEventById(
      { scheduledEvents: scheduledEventsState },
      eventId
    );
  };

  const cachedBounds =
    useAppSelector(selectTabPreferences)?.tabBounds[CACHED_TAB_KEY];
  const [bounds, setBounds] = useState<PaginationBounds>(() => {
    if (cachedBounds) return cachedBounds;
    return {
      pageSize: rowsPerPageOptions[0],
      pageStart: 0,
    };
  });

  useEffect(() => {
    void dispatch(
      docketActions.setDocketTabBounds({ tab: CACHED_TAB_KEY, bounds })
    );
  }, [bounds]);

  // Set page size to 10 when switching to mobile
  useEffect(() => {
    if (isMobile && bounds.pageSize !== 10) {
      setBounds(prevBounds => ({
        ...prevBounds,
        pageSize: 10,
        pageStart: 0,
      }));
      setSortParams(prevParams => ({
        ...prevParams,
        pageSize: 10,
        pageStart: 0,
      }));
    }
  }, [isMobile, bounds.pageSize]);

  const { processedData, filteredCount } = useMemo(() => {
    // filter and convert
    let pendingItemsWithMember = latePendingItems
      .filter(p => isPendingItemInFilter(p, filters))
      .map(p => convertToPendingItemWithMember(p, memberMap));
    const filteredCount = pendingItemsWithMember.length;
    if (sortParams) {
      // apply sort
      const comparator = getPendingItemComparator(
        sortParams.orderDirection,
        sortParams.orderBy as PendingColumnKeys
      );
      pendingItemsWithMember.sort(comparator);

      // apply pagination
      const startIndex = sortParams.pageStart * sortParams.pageSize;
      pendingItemsWithMember = pendingItemsWithMember.slice(
        startIndex,
        startIndex + sortParams.pageSize
      );
    }
    return { processedData: pendingItemsWithMember, filteredCount };
  }, [latePendingItems, sortParams, filters]);

  // Mobile menu handlers
  const handleOpenMobileMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: PendingItemWithMember
  ) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedMobileItem(item);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
    setSelectedMobileItem(null);
  };

  const handleMobileMenuItemClick = async (callback: () => void) => {
    await callback();
    handleCloseMobileMenu();
  };

  // Get menu items for pending item
  const getMenuItemsForPendingItem = (data: PendingItemWithMember) => {
    const scheduledEvent =
      data.externalItemType === 'scheduled_event'
        ? getScheduledEvent(data.externalItemId)
        : null;

    if (data?.externalItemType === 'scheduled_event' && scheduledEvent) {
      const menuItems = [];
      switch (scheduledEvent.type) {
        case 'visit':
          menuItems.push({
            display: 'Record visit now',
            callback: () =>
              interactionsDialogHelper.createVisit({
                userId: scheduledEvent.userId,
                scheduledEvent,
              }),
          });
          break;
        case 'call':
          menuItems.push({
            display: 'Call now',
            callback: () =>
              interactionsDialogHelper.createCall({
                scheduledEvent,
                user: data.member,
              }),
          });
          break;
        case 'message':
          menuItems.push({
            display: 'Message now',
            callback: () =>
              interactionsDialogHelper.createMessage({
                scheduledEventId: scheduledEvent.id,
                user: data.member,
              }),
          });
      }
      menuItems.push({
        display: 'Reschedule event',
        callback: () =>
          interactionsDialogHelper.editScheduledEvent({
            scheduledEvent,
            user: data.member,
          }),
      });
      menuItems.push({
        display: 'Delete event',
        callback: () =>
          deleteScheduledEvent({
            params: {
              userId: data.memberId,
              scheduledEventId: scheduledEvent.id,
            },
          }),
      });
      return menuItems;
    } else {
      return [
        {
          display: 'View request',
          callback: () =>
            toMemberPage(
              data.member.assignedGuideId,
              data.member.id,
              data.externalItemType && data.externalItemId
                ? {
                    externalType: data.externalItemType,
                    externalId: data.externalItemId,
                  }
                : undefined
            ),
        },
      ];
    }
  };

  // Get visible menu items for selected mobile item
  const visibleMenuItems = useMemo(() => {
    if (!selectedMobileItem) return [];
    return getMenuItemsForPendingItem(selectedMobileItem);
  }, [selectedMobileItem]);

  return (
    <FlexContainer
      direction="column"
      padding={isMobile ? '16px' : '16px 16px 0'}
      height={isMobile ? 'auto' : '100%'}
      width="100%"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'visible',
      }}
    >
      <FilterChips
        filterChoices={filterChoices}
        setSelectedFilters={setFilters}
      />
      {isMobile ? (
        <>
          <FlexContainer
            direction="column"
            gap={2}
            width="100%"
            padding="16px 0 0 0"
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {processedData.map(item => {
              const scheduledEvent =
                item.externalItemType === 'scheduled_event'
                  ? getScheduledEvent(item.externalItemId)
                  : null;
              const { date, time } = formatLateItemTime(item, scheduledEvent);
              const eventName = eventTypeToLabel[item.eventType];

              const handleGotoAction = () => {
                toMemberPage(
                  item.member.assignedGuideId,
                  item.member.id,
                  item.externalItemType && item.externalItemId
                    ? {
                        externalType: item.externalItemType,
                        externalId: item.externalItemId,
                      }
                    : undefined
                );
              };

              return (
                <DocketMobileCard
                  key={`${item.externalItemType}-${item.externalItemId}`}
                  member={item.member}
                  topic={eventName}
                  date={date}
                  time={time}
                  gotoAction={handleGotoAction}
                  openMenu={e => handleOpenMobileMenu(e, item)}
                />
              );
            })}
            <DocketMobilePagination
              page={bounds.pageStart}
              pageSize={bounds.pageSize}
              totalItems={filteredCount}
              onPageChange={(newPage: number) => {
                setBounds(prevBounds => ({
                  ...prevBounds,
                  pageStart: newPage,
                }));
                setSortParams(prevParams => ({
                  ...prevParams,
                  pageStart: newPage,
                }));
              }}
            />
          </FlexContainer>
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleCloseMobileMenu}
          >
            {visibleMenuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => handleMobileMenuItemClick(item.callback)}
              >
                {item.display}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <ScrollableTable
          data={processedData}
          stickyHeader={false}
          dataRowElement={props =>
            PendingItemTableRow({ ...props, showDate: true })
          }
          headerCells={headerCellConfig}
          totalRecords={filteredCount}
          dispatchAction={setSortParams}
          rowKeyGenerator={entry =>
            `${entry.externalItemType}-${entry.externalItemId}`
          }
          isLoading={loading}
          bounds={bounds}
          setBounds={setBounds}
        />
      )}
    </FlexContainer>
  );
};

export default DocketLateEvents;
